import type {
  Operator,
  OperatorCreate,
  OperatorUpdate,
} from "@/providers/smarta-accounts/accounts-api-client";

import { client } from "./client";

const OPERATORS_PAGE_SIZE = 100;

/**
 * Fetch every operator matching the filter, paging through the endpoint with
 * `limit` / `offset`. Mirrors `listCustomerPublicIdsManagedBy` in the backend,
 * which pages the sibling customers endpoint the same way.
 *
 * This used to take a single max-size page and throw when `pagination.total`
 * said there were more, on the reasoning that a truncated list would silently
 * drop valid assignees. Throwing solved that for the picker, where a hard
 * failure is visible, but not for the Action log, which catches the error and
 * falls back to raw ids — so the moment the count passed one page, every name
 * in the log would quietly turn back into a UUID. Paging properly removes the
 * choice between a truncated list and a broken one.
 *
 * Archived operators count toward the total, so the list that includes them
 * grows for good and will reach a second page long before the active one does.
 */
const fetchAllOperators = async (archived?: "false"): Promise<Operator[]> => {
  const operators: Operator[] = [];

  for (let offset = 0; ; offset += OPERATORS_PAGE_SIZE) {
    const { data, error } = await client.GET("/api/operators", {
      params: {
        query: {
          limit: OPERATORS_PAGE_SIZE,
          offset,
          ...(archived ? { archived } : {}),
        },
      },
    });
    if (error || !data?.data) {
      throw error || new Error("Malformed list-operators response");
    }

    const page = data.data;
    operators.push(...page);

    if (page.length < OPERATORS_PAGE_SIZE) {
      return operators;
    }
  }
};

/**
 * List the active operators from smarta-accounts through the generated client.
 * Mirrors how the banking module calls its accounts-backed endpoints — no
 * hand-written axios path.
 *
 * `archived: false` excludes archived (deleted) operators — the list endpoint
 * returns both by default, but the picker / filter / management table must only
 * show people who can still be assigned work.
 */
export const listOperators = async (): Promise<Operator[]> =>
  fetchAllOperators("false");

/**
 * List operators including archived ones, by omitting the `archived` filter
 * (the endpoint returns both by default).
 *
 * Only for *reading back* an operator id that was written in the past, such as
 * rendering a name in the Action log. An assignment made years ago can point at
 * someone who has since left, and those are exactly the entries a reader opens
 * the log for; `listOperators` would leave them as raw UUIDs. Do NOT use this
 * to populate a picker or a filter — an archived operator must not be offered
 * as a new assignee.
 */
export const listOperatorsIncludingArchived = async (): Promise<Operator[]> =>
  fetchAllOperators();

/** Create an operator via `POST /api/operators`. */
export const createOperator = async (
  payload: OperatorCreate
): Promise<Operator> => {
  const { data, error } = await client.POST("/api/operators", {
    body: payload,
  });
  if (error || !data?.data) {
    throw error || new Error("Malformed create-operator response");
  }
  return data.data;
};

/** Update an operator via `PATCH /api/operators/{id}`. */
export const updateOperator = async (
  id: string,
  payload: OperatorUpdate
): Promise<Operator> => {
  const { data, error } = await client.PATCH("/api/operators/{id}", {
    params: { path: { id } },
    body: payload,
  });
  if (error || !data?.data) {
    throw error || new Error("Malformed update-operator response");
  }
  return data.data;
};

/** Delete an operator via `DELETE /api/operators/{id}`. */
export const deleteOperator = async (id: string): Promise<void> => {
  const { error } = await client.DELETE("/api/operators/{id}", {
    params: { path: { id } },
  });
  if (error) {
    throw error;
  }
};
